// @ts-nocheck
import React, { useState, useEffect } from 'react';

const _t = () => (window).__ENGPORTAL_THEME || null;
const _dark = { bg: "#0B1120", surface: "#111827", surfaceHover: "#1a2332", border: "#1E293B", borderLight: "#334155", text: "#E2E8F0", textMuted: "#94A3B8", textDim: "#64748B", accent: "#3B82F6", accentGlow: "rgba(59, 130, 246, 0.15)", green: "#10B981", yellow: "#F59E0B", red: "#EF4444", purple: "#8B5CF6", orange: "#F97316", cardShadow: "none", mode: "dark" };
const theme = new Proxy(_dark, { get: (fallback, prop) => { const t = _t(); return t ? t[prop] : fallback[prop]; }});
const csrfToken = () => document.cookie.split('csrftoken=')[1]?.split(';')[0] || '';

// Task priority — business importance, independent of due date.
const PRIO_RANK = { urgent: 0, high: 1, normal: 2, low: 3 };
const PRIO_LABEL = { urgent: 'Urgent', high: 'High', normal: 'Normal', low: 'Low' };
// Color per priority; normal returns null so it shows no tag (keeps cards clean).
const prioColor = (p) => ({ urgent: theme.red, high: theme.orange, low: theme.textMuted }[p] || null);

// Filterable single-select people picker: type to search instead of scrolling
// a long list. Reused for Assigned To, Shared With (add), and checklist assignee.
function UserSelect({ value, onChange, users, exclude = [], placeholder = 'Select…', allowClear = true, keepOpen = false, compact = false }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef(null);
  const nameOf = (id) => { const u = users.find(x => x.id === id); return u ? (`${u.first_name} ${u.last_name}`.trim() || u.username) : ''; };
  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const q = query.trim().toLowerCase();
  const list = users.filter(u => {
    if (exclude.includes(u.id)) return false;
    if (!q) return true;
    return (`${u.first_name || ''} ${u.last_name || ''} ${u.username || ''}`).toLowerCase().includes(q);
  }).slice(0, 50);
  const pick = (id) => { onChange(id); setQuery(''); if (!(keepOpen && id)) setOpen(false); };
  const fs = compact ? 11 : 13;
  const pad = compact ? '4px 6px' : '8px 10px';
  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <input
        value={open ? query : nameOf(value)}
        placeholder={nameOf(value) && !open ? '' : placeholder}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
        style={{ width: '100%', padding: pad, background: theme.bg, border: `1px solid ${value ? theme.accent + '55' : theme.border}`, borderRadius: 6, color: value ? theme.text : theme.textDim, fontSize: fs, outline: 'none', boxSizing: 'border-box' }}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60, marginTop: 2, maxHeight: 220, overflowY: 'auto', background: theme.surface, border: `1px solid ${theme.borderLight || theme.border}`, borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
          {allowClear && value ? (
            <div onMouseDown={e => { e.preventDefault(); pick(null); }} style={{ padding: pad, fontSize: fs, color: theme.textDim, cursor: 'pointer', borderBottom: `1px solid ${theme.border}` }}>— Clear —</div>
          ) : null}
          {list.length === 0 ? (
            <div style={{ padding: pad, fontSize: fs, color: theme.textDim }}>No matches</div>
          ) : list.map(u => (
            <div key={u.id} onMouseDown={e => { e.preventDefault(); pick(u.id); }}
              style={{ padding: pad, fontSize: fs, color: theme.text, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = theme.surfaceHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {u.first_name} {u.last_name} <span style={{ color: theme.textDim }}>({u.username})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FlaggedPicker({ value, onChange, users, currentUserId, max = 8 }) {
  const selected = value || [];
  const nameOf = (id) => { const u = users.find(x => x.id === id); return u ? (`${u.first_name} ${u.last_name}`.trim() || u.username) : id; };
  return (
    <div>
      <label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>
        Shared With <span style={{ fontWeight: 400 }}>({selected.length}/{max})</span>
      </label>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {selected.map(id => (
            <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: theme.accent + '18', color: theme.accent, borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600 }}>
              {nameOf(id)}
              <span onClick={() => onChange(selected.filter(x => x !== id))} style={{ cursor: 'pointer', fontWeight: 700 }}>×</span>
            </span>
          ))}
        </div>
      )}
      {selected.length >= max ? (
        <div style={{ padding: '8px 12px', fontSize: 12, color: theme.textDim, border: `1px solid ${theme.border}`, borderRadius: 6 }}>Maximum {max} reached</div>
      ) : (
        <UserSelect
          value={null}
          onChange={id => { if (id && !selected.includes(id)) onChange([...selected, id]); }}
          users={users}
          exclude={[...selected, currentUserId].filter(Boolean)}
          placeholder="+ Add viewer… (type to search)"
          allowClear={false}
          keepOpen={true}
        />
      )}
    </div>
  );
}


function TaskAttachments({ task, currentUser }) {
  const [items, setItems] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const fileRef = React.useRef(null);
  const MAX = 3;

  const reload = async () => {
    try {
      const d = await fetch('/api/sprints/tasks/' + task.id + '/attach/', { credentials: 'same-origin' })
        .then(r => r.ok ? r.json() : []);
      setItems(Array.isArray(d) ? d : []);
    } catch (e) { /* noop */ }
  };
  React.useEffect(() => { reload(); }, [task.id]);

  const canRemove = (a) => {
    if (!currentUser) return false;
    const isManager = currentUser.role === 'engineering_manager' || currentUser.role === 'executive';
    const isUploader = a.uploaded_by_id === currentUser.id;
    const isOwner = task.assigned_to === currentUser.id || task.created_by === currentUser.id;
    return isManager || isUploader || isOwner;
  };

  const upload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (items.length >= MAX) { setErr('Maximum of 3 attachments per task.'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      const res = await fetch('/api/sprints/tasks/' + task.id + '/attach/', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'X-CSRFToken': csrfToken() }, body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setErr(d.detail || 'Upload failed.');
      } else {
        await reload();
      }
    } catch (e2) { setErr('Upload failed.'); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const remove = async (a) => {
    if (!window.confirm('Remove "' + a.title + '"?')) return;
    try {
      const res = await fetch('/api/sprints/tasks/' + task.id + '/attach/?attachment_id=' + a.id, {
        method: 'DELETE', credentials: 'same-origin', headers: { 'X-CSRFToken': csrfToken() },
      });
      if (res.ok) reload();
    } catch (e) { /* noop */ }
  };

  const shortName = (a) => {
    const base = (a.filename || a.title || 'file').split('/').pop();
    return a.title && a.title.length <= 40 ? a.title : base;
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Attachments {items.length > 0 && <span style={{ color: theme.textMuted }}>({items.length}/{MAX})</span>}
        </span>
        {items.length < MAX && (
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.accent, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Uploading…' : '+ Attach file'}
            <input ref={fileRef} type="file" onChange={upload} disabled={busy} style={{ display: 'none' }} />
          </label>
        )}
      </div>
      {err && <div style={{ color: theme.red || '#EF4444', fontSize: 11, marginBottom: 6 }}>{err}</div>}
      {items.length === 0 ? (
        <div style={{ color: theme.textDim, fontSize: 12 }}>No attachments yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 6 }}>
              <a href={'/api/sprints/tasks/' + task.id + '/attach/' + a.id + '/view/'} target="_blank" rel="noopener noreferrer"
                style={{ color: theme.accent, fontSize: 12, fontWeight: 600, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
                title={a.title}>
                📎 {shortName(a)}
              </a>
              <span style={{ color: theme.textDim, fontSize: 10, whiteSpace: 'nowrap' }}>{a.uploaded_by || ''}</span>
              <a href={'/api/sprints/tasks/' + task.id + '/attach/' + a.id + '/download/'} download title="Download"
                style={{ color: theme.textDim, fontSize: 11, textDecoration: 'none', whiteSpace: 'nowrap' }}>↓</a>
              {canRemove(a) && (
                <button onClick={() => remove(a)} title="Remove"
                  style={{ background: 'none', border: 'none', color: theme.textDim, cursor: 'pointer', fontSize: 15, padding: '0 2px' }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskChecklist({ task, onRefresh, users = [], currentUser }) {
  const [items, setItems] = React.useState(task.checklist_items || []);
  const [newText, setNewText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editText, setEditText] = React.useState('');
  React.useEffect(() => { setItems(task.checklist_items || []); }, [task.id, task.checklist_items]);

  const shortName = (id) => { const u = users.find(x => x.id === id); return u ? (`${u.first_name} ${u.last_name}`.trim() || u.username) : ''; };

  const done = items.filter(i => i.is_done).length;

  const reload = async () => {
    try {
      const d = await fetch('/api/sprints/checklist-items/?task=' + task.id, { credentials: 'same-origin' }).then(r => r.ok ? r.json() : []);
      setItems(Array.isArray(d) ? d : (d.results || []));
      onRefresh && onRefresh();
    } catch (e) { /* noop */ }
  };

  const toggle = async (item) => {
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_done: !i.is_done } : i)); // optimistic
    try {
      await fetch('/api/sprints/checklist-items/' + item.id + '/toggle/', { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRFToken': csrfToken() } });
      reload();
    } catch (e) { reload(); }
  };

  const addItem = async () => {
    const text = newText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await fetch('/api/sprints/checklist-items/', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() },
        body: JSON.stringify({ task: task.id, text, order: items.length }),
      });
      setNewText('');
      reload();
    } catch (e) { /* noop */ }
    setBusy(false);
  };

  const removeItem = async (id) => {
    try { await fetch('/api/sprints/checklist-items/' + id + '/', { method: 'DELETE', credentials: 'same-origin', headers: { 'X-CSRFToken': csrfToken() } }); reload(); } catch (e) { /* noop */ }
  };

  const assign = async (item, assigneeId) => {
    const val = assigneeId ? parseInt(assigneeId, 10) : null;
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, assignee: val, assignee_name: val ? shortName(val) : null } : i)); // optimistic
    try {
      await fetch('/api/sprints/checklist-items/' + item.id + '/', {
        method: 'PATCH', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() },
        body: JSON.stringify({ assignee: val }),
      });
      reload();
    } catch (e) { reload(); }
  };

  const startEdit = (item) => { setEditingId(item.id); setEditText(item.text); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async (item) => {
    const text = editText.trim();
    if (!text || text === item.text) { cancelEdit(); return; }
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, text } : i)); // optimistic
    setEditingId(null);
    try {
      await fetch('/api/sprints/checklist-items/' + item.id + '/', {
        method: 'PATCH', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() },
        body: JSON.stringify({ text }),
      });
      reload();
    } catch (e) { reload(); }
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>Checklist</span>
        {items.length > 0 && <span style={{ color: theme.textMuted, fontSize: 11 }}>{done} of {items.length} done</span>}
      </div>
      {items.map(item => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${theme.border}` }}>
          <input type="checkbox" checked={item.is_done} onChange={() => toggle(item)} style={{ cursor: 'pointer', width: 16, height: 16 }} />
          {editingId === item.id ? (
            <input
              autoFocus
              value={editText}
              onChange={e => setEditText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveEdit(item); } else if (e.key === 'Escape') { cancelEdit(); } }}
              onBlur={() => saveEdit(item)}
              style={{ flex: 1, fontSize: 13, padding: '3px 6px', background: theme.bg, border: `1px solid ${theme.accent}`, borderRadius: 4, color: theme.text, outline: 'none' }}
            />
          ) : (
            <span
              onDoubleClick={() => startEdit(item)}
              title="Double-click to edit"
              style={{ flex: 1, fontSize: 13, color: item.is_done ? theme.textDim : theme.text, textDecoration: item.is_done ? 'line-through' : 'none', cursor: 'text' }}>{item.text}</span>
          )}
          {editingId !== item.id && (
            <button onClick={() => startEdit(item)} title="Edit" style={{ background: 'none', border: 'none', color: theme.textDim, cursor: 'pointer', fontSize: 12, padding: 0 }}>✎</button>
          )}
          <div style={{ width: 150, flex: 'none' }} title="Assign this item to someone">
            <UserSelect value={item.assignee || null} onChange={id => assign(item, id)} users={users} placeholder="Assign…" compact={true} />
          </div>
          {item.done_by_name && item.is_done && <span style={{ fontSize: 10, color: theme.textDim }}>{item.done_by_name}</span>}
          <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: theme.textDim, cursor: 'pointer', fontSize: 15 }}>×</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input value={newText} onChange={e => setNewText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
          placeholder="Add a checklist item"
          style={{ flex: 1, padding: '7px 10px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 13, outline: 'none' }} />
        <button onClick={addItem} disabled={busy} style={{ padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: theme.accent, color: '#fff', border: 'none', cursor: 'pointer' }}>Add</button>
      </div>
    </div>
  );
}

export default function KanbanPage() {
  const iStyle = { width: '100%', padding: '8px 12px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const statusCols = [
    { key: 'todo', label: 'To Do', color: theme.textDim },
    { key: 'in_progress', label: 'In Progress', color: theme.accent },
    { key: 'in_review', label: 'In Review', color: theme.yellow },
    { key: 'blocked', label: 'Blocked', color: theme.red },
    { key: 'done', label: 'Done', color: theme.green },
  ];
  const [tasks, setTasks] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', task_type: 'other', assigned_to: '', sprint: '', due_date: '', priority: 'normal', flagged_users: [], checklist: [] });
  const [saving, setSaving] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [ownedOnly, setOwnedOnly] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const [viewMode, setViewMode] = useState('board');
  // "My Actions" view: open checklist lines assigned to me (or everyone, for managers).
  const [actionItems, setActionItems] = useState([]);
  const [actionScope, setActionScope] = useState('mine');
  const [actionsLoading, setActionsLoading] = useState(false);

  const loadData = async () => {
    const f = (url) => fetch(url, { credentials: 'same-origin' }).then(r => r.ok ? r.json() : null).catch(() => null);
    // Follow DRF pagination (`next` links) so lists over one page (25) load fully.
    const fetchAllPages = async (startUrl) => {
      let out = [], url = startUrl, guard = 0;
      while (url && guard < 200) {
        guard++;
        let d;
        try { const r = await fetch(url, { credentials: 'same-origin' }); if (!r.ok) break; d = await r.json(); }
        catch (e) { break; }
        if (Array.isArray(d)) { out = out.concat(d); break; }
        out = out.concat(d.results || []);
        if (d.next) { try { const u = new URL(d.next); url = u.pathname + u.search; } catch (e) { url = d.next; } }
        else url = null;
      }
      return out;
    };
    const params = [];
    if (showAll) params.push('show_all=true');
    if (showArchived) params.push('show_archived=true');
    const taskUrl = '/api/sprints/tasks/' + (params.length ? '?' + params.join('&') : '');
    const [tk, sp, rawUsers, me] = await Promise.all([
      fetchAllPages(taskUrl),
      fetchAllPages('/api/sprints/sprints/'),
      fetchAllPages('/api/auth/users/'),
      f('/api/auth/session-login/'),
    ]);
    setTasks(tk);
    setSprints(sp);
    rawUsers.sort((a, b) => {
      if (a.username === 'JayM913') return -1;
      if (b.username === 'JayM913') return 1;
      const an = (`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.username || '').toLowerCase();
      const bn = (`${b.first_name || ''} ${b.last_name || ''}`.trim() || b.username || '').toLowerCase();
      return an.localeCompare(bn);
    });
    setUsers(rawUsers);
    if (me && me.id) setCurrentUser(me);
    setLoading(false);
  };
  useEffect(() => { loadData(); }, [showAll, showArchived]);

  const isManager = currentUser?.role === 'engineering_manager' || currentUser?.role === 'executive';

  // Load open assigned checklist lines for the "My Actions" view.
  const loadActions = async (scope) => {
    setActionsLoading(true);
    try {
      const d = await fetch('/api/sprints/my-actions/?scope=' + (scope || actionScope), { credentials: 'same-origin' })
        .then(r => r.ok ? r.json() : null);
      setActionItems((d && d.items) ? d.items : []);
    } catch (e) { setActionItems([]); }
    setActionsLoading(false);
  };
  useEffect(() => { if (viewMode === 'myactions') loadActions(actionScope); }, [viewMode, actionScope]);

  // Tick an action item done → it leaves the open list; refresh board counts too.
  const tickAction = async (item) => {
    setActionItems(prev => prev.filter(a => a.id !== item.id)); // optimistic
    try {
      await fetch('/api/sprints/checklist-items/' + item.id + '/toggle/', { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRFToken': csrfToken() } });
      loadData();
    } catch (e) { loadActions(actionScope); }
  };

  // Open the parent task from a My Actions row (fetch it if not in the loaded set).
  const openTaskById = async (taskId) => {
    const found = tasks.find(t => t.id === taskId);
    if (found) { setSelectedTask(found); return; }
    try {
      const d = await fetch('/api/sprints/tasks/' + taskId + '/', { credentials: 'same-origin' }).then(r => r.ok ? r.json() : null);
      if (d) setSelectedTask(d);
    } catch (e) { /* noop */ }
  };

  // Group action items: by parent task (mine) or by person (everyone).
  const actionGroups = (() => {
    const byEveryone = actionScope === 'everyone';
    const map = {}, order = [];
    (actionItems || []).forEach(a => {
      const key = byEveryone ? ('u' + a.assignee_id) : ('t' + a.task_id);
      const label = byEveryone ? (a.assignee_name || 'Unassigned') : a.task_title;
      if (!map[key]) { map[key] = { key, label, items: [] }; order.push(key); }
      map[key].items.push(a);
    });
    return order.map(k => map[k]);
  })();
  // Server enforces base visibility (owner + assignee + flagged; managers can Show all).
  // "Limit to owned" is a client-side display filter: keep only tasks where the
  // current user is the assignee or the creator, hiding ones they only view (flagged).
  const visibleTasks = (ownedOnly && currentUser?.id)
    ? tasks.filter(t => t.assigned_to === currentUser.id || t.created_by === currentUser.id)
    : tasks;

  // Search (client-side, across the fields that matter) + sort. Both views share
  // this result so they stay consistent.
  const sprintName = (id) => sprints.find(s => s.id === id)?.name || '';
  const q = searchText.trim().toLowerCase();
  const searchedTasks = q
    ? visibleTasks.filter(t => {
        const hay = [
          t.title, t.description, t.assigned_to_name, t.task_type,
          sprintName(t.sprint), t.work_order,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
    : visibleTasks;

  const sortedTasks = (() => {
    const arr = [...searchedTasks];
    const cmpStr = (a, b) => (a || '').localeCompare(b || '');
    const cmpDue = (a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date);
    };
    if (sortBy === 'priority') {
      // Urgent→Low, then soonest due within each level (undated last).
      arr.sort((a, b) => {
        const ra = PRIO_RANK[a.priority] ?? 2, rb = PRIO_RANK[b.priority] ?? 2;
        return ra !== rb ? ra - rb : cmpDue(a, b);
      });
    } else if (sortBy === 'due_date') {
      // Soonest first; undated tasks sink to the bottom.
      arr.sort(cmpDue);
    } else if (sortBy === 'created') {
      arr.sort((a, b) => cmpStr(b.created_at, a.created_at)); // newest first
    } else if (sortBy === 'effort') {
      arr.sort((a, b) => (b.effort_points || 0) - (a.effort_points || 0)); // high → low
    } else if (sortBy === 'title') {
      arr.sort((a, b) => cmpStr(a.title, b.title));
    } else if (sortBy === 'assignee') {
      arr.sort((a, b) => cmpStr(a.assigned_to_name, b.assigned_to_name) || cmpStr(a.title, b.title));
    }
    return arr;
  })();

  const moveTask = async (taskId, newStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    if (selectedTask?.id === taskId) setSelectedTask(prev => ({ ...prev, status: newStatus }));
    try {
      await fetch('/api/sprints/tasks/' + taskId + '/', { method: 'PATCH', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() }, body: JSON.stringify({ status: newStatus }) });
      loadData();
    } catch(e) { loadData(); }
  };

  const createTask = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      const payload = { title: form.title, description: form.description, task_type: form.task_type, status: 'todo', priority: form.priority || 'normal' };
      if (form.assigned_to) payload.assigned_to = form.assigned_to;
      if (form.sprint) payload.sprint = form.sprint;
      if (form.due_date) payload.due_date = form.due_date;
      payload.flagged_users = form.flagged_users || [];
      const res = await fetch('/api/sprints/tasks/', { method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() }, body: JSON.stringify(payload) });
      if (res.ok) {
        // Create any checklist items entered on the create form, against the new task.
        const created = await res.json();
        const items = (form.checklist || []).map(s => (s || '').trim()).filter(Boolean);
        if (created && created.id && items.length) {
          for (let i = 0; i < items.length; i++) {
            try {
              await fetch('/api/sprints/checklist-items/', { method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() },
                body: JSON.stringify({ task: created.id, text: items[i], order: i }) });
            } catch (e) { /* best-effort; task already created */ }
          }
        }
        setShowCreate(false);
        setForm({ title: '', description: '', task_type: 'other', assigned_to: '', sprint: '', due_date: '', priority: 'normal', flagged_users: [], checklist: [] });
        loadData();
      }
      else { alert('Error: ' + await res.text()); }
    } catch(e) { alert('Failed'); }
    setSaving(false);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (payload.assigned_to === '') payload.assigned_to = null;
      if (payload.sprint === '') payload.sprint = null;
      const res = await fetch('/api/sprints/tasks/' + selectedTask.id + '/', { method: 'PATCH', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() }, body: JSON.stringify(payload) });
      if (res.ok) { const updated = await res.json(); setSelectedTask(updated); setEditing(false); loadData(); }
      else { alert('Error: ' + await res.text()); }
    } catch(e) { alert('Failed'); }
    setSaving(false);
  };

  const deleteTask = async (taskId) => {
    if (!confirm('Delete this task?')) return;
    await fetch('/api/sprints/tasks/' + taskId + '/', { method: 'DELETE', credentials: 'same-origin', headers: { 'X-CSRFToken': csrfToken() } });
    setSelectedTask(null); loadData();
  };

  const toggleArchive = async (t) => {
    const verb = t.archived ? 'unarchive' : 'archive';
    await fetch('/api/sprints/tasks/' + t.id + '/' + verb + '/', { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRFToken': csrfToken() } });
    setSelectedTask(null); loadData();
  };

  const startEdit = () => {
    setEditForm({ title: selectedTask.title||'', description: selectedTask.description||'', task_type: selectedTask.task_type||'other',
      assigned_to: selectedTask.assigned_to||'', sprint: selectedTask.sprint||'', due_date: selectedTask.due_date||'', effort_points: selectedTask.effort_points||1,
      priority: selectedTask.priority || 'normal',
      flagged_users: selectedTask.flagged_users || [] });
    setEditing(true);
  };

  const handleDragStart = (e, task) => { setDraggedTask(task); e.dataTransfer.effectAllowed = 'move'; setTimeout(() => { e.target.style.opacity = '0.4'; }, 0); };
  const handleDragEnd = (e) => { e.target.style.opacity = '1'; setDraggedTask(null); setDragOverCol(null); };
  const handleDragOver = (e, colKey) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverCol !== colKey) setDragOverCol(colKey); };
  const handleDragLeave = (e) => { const rect = e.currentTarget.getBoundingClientRect(); const x = e.clientX, y = e.clientY; if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) setDragOverCol(null); };
  const handleDrop = (e, colKey) => { e.preventDefault(); setDragOverCol(null); if (draggedTask && draggedTask.status !== colKey) moveTask(draggedTask.id, colKey); setDraggedTask(null); };

  if (loading) return <div style={{ color: theme.textDim, padding: 40, textAlign: 'center' }}>Loading...</div>;

  if (selectedTask) {
    const t = selectedTask;
    const sc = statusCols.find(c => c.key === t.status) || { color: theme.textDim, label: t.status };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => { setSelectedTask(null); setEditing(false); }} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: 6, padding: '6px 12px', color: theme.textMuted, cursor: 'pointer', fontSize: 12 }}>← Back</button>
          <span style={{ background: sc.color+'22', color: sc.color, fontSize: 11, padding: '3px 10px', borderRadius: 6, fontWeight: 600 }}>{sc.label}</span>
          {t.is_work_order && <span style={{ background: theme.purple+'22', color: theme.purple, fontSize: 11, padding: '3px 10px', borderRadius: 6, fontWeight: 600 }}>Linked to {t.work_order_number || 'Work Order'}</span>}
        </div>
        <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 24, flex: 1, overflow: 'auto' }}>
          {editing ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ color: theme.text, fontSize: 18, fontWeight: 700, margin: 0 }}>Edit Task</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setEditing(false)} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 12, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={saveEdit} disabled={saving} style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: theme.accent, color: '#fff', border: 'none', cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div><label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Title</label><input value={editForm.title} onChange={e => setEditForm(p=>({...p, title: e.target.value}))} style={iStyle} /></div>
                <div><label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Description</label><textarea value={editForm.description} onChange={e => setEditForm(p=>({...p, description: e.target.value}))} style={{...iStyle, height: 80, resize:'vertical'}} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Assigned To</label><UserSelect value={editForm.assigned_to || null} onChange={id => setEditForm(p=>({...p, assigned_to: id || ''}))} users={users} placeholder="Unassigned — type to search" /></div>
                  <div><label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Task Type</label><select value={editForm.task_type} onChange={e => setEditForm(p=>({...p, task_type: e.target.value}))} style={{...iStyle, appearance:'auto'}}>{['development','testing','documentation','review','manufacturing','other'].map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}</select></div>
                  <div><label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Sprint</label><select value={editForm.sprint} onChange={e => setEditForm(p=>({...p, sprint: e.target.value}))} style={{...iStyle, appearance:'auto'}}><option value="">No sprint</option>{sprints.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                  <div><label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Due Date</label><input type="date" value={editForm.due_date} onChange={e => setEditForm(p=>({...p, due_date: e.target.value}))} style={iStyle} /></div>
                  <div><label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Effort Points</label><input type="number" min="1" max="8" value={editForm.effort_points} onChange={e => setEditForm(p=>({...p, effort_points: parseInt(e.target.value)||1}))} style={iStyle} /></div>
                  <div><label style={{ color: theme.textDim, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority {!isManager && <span style={{ fontWeight: 400, color: theme.textDim }}>(set by management)</span>}</label>
                    {isManager ? (
                      <select value={editForm.priority || 'normal'} onChange={e => setEditForm(p=>({...p, priority: e.target.value}))} style={{...iStyle, appearance:'auto'}}>{['urgent','high','normal','low'].map(pr => <option key={pr} value={pr}>{PRIO_LABEL[pr]}</option>)}</select>
                    ) : (
                      <div style={{...iStyle, color: prioColor(editForm.priority) || theme.text, display: 'flex', alignItems: 'center'}}>{PRIO_LABEL[editForm.priority || 'normal']}</div>
                    )}
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}><FlaggedPicker value={editForm.flagged_users} onChange={v => setEditForm(p=>({...p, flagged_users: v}))} users={users} currentUserId={currentUser?.id} /></div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ color: theme.text, fontSize: 20, fontWeight: 700, margin: 0 }}>{t.title}</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={startEdit} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 11, background: theme.accent+'15', color: theme.accent, border: `1px solid ${theme.accent}33`, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                  <button onClick={() => toggleArchive(t)} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 11, background: theme.bg, color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer' }}>{t.archived ? 'Unarchive' : 'Archive'}</button>
                  {!t.is_work_order && <button onClick={() => deleteTask(t.id)} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 11, background: theme.red+'15', color: theme.red, border: `1px solid ${theme.red}33`, cursor: 'pointer' }}>Delete</button>}
                </div>
              </div>
              {t.description && <div style={{ color: theme.textMuted, fontSize: 13, lineHeight: 1.7, background: theme.bg, padding: 14, borderRadius: 8, border: `1px solid ${theme.border}`, marginBottom: 16, whiteSpace: 'pre-wrap' }}>{t.description}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                {[['Assigned To', t.assigned_to_name || 'Unassigned'], ['Type', (t.task_type||'').replace(/_/g,' ')], ['Due Date', t.due_date || '—'], ['Sprint', sprints.find(s=>s.id===t.sprint)?.name || '—'], ['Effort', t.effort_points || '—'], ['Created', t.created_at ? new Date(t.created_at).toLocaleDateString() : '—']].map(([label, val]) => (
                  <div key={label} style={{ background: theme.bg, borderRadius: 8, padding: '10px 14px', border: `1px solid ${theme.border}` }}>
                    <div style={{ color: theme.textDim, fontSize: 10, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                    <div style={{ color: theme.text, fontSize: 13, textTransform: 'capitalize' }}>{val}</div>
                  </div>
                ))}
              </div>
              {(t.flagged_users_detail && t.flagged_users_detail.length > 0) && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Shared With</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {t.flagged_users_detail.map(u => <span key={u.id} style={{ background: theme.accent+'18', color: theme.accent, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{u.name}</span>)}
                  </div>
                </div>
              )}
              <TaskChecklist task={t} onRefresh={loadData} users={users} currentUser={currentUser} />

              <TaskAttachments task={t} currentUser={currentUser} />

              <div style={{ color: theme.textDim, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 10 }}>Move To</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {statusCols.filter(c => c.key !== t.status).map(c => (
                  <button key={c.key} onClick={() => { moveTask(t.id, c.key); setSelectedTask({...t, status: c.key}); }}
                    style={{ padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: c.color, color: '#fff', border: 'none', cursor: 'pointer' }}>{c.label}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ color: theme.text, fontSize: 20, fontWeight: 700, margin: 0 }}>Task Board {!showAll && <span style={{ fontSize: 12, fontWeight: 400, color: theme.textDim }}>(My Tasks)</span>}{showAll && <span style={{ fontSize: 12, fontWeight: 400, color: theme.textDim }}>(All Tasks)</span>}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.textMuted, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={ownedOnly} onChange={e => setOwnedOnly(e.target.checked)} />
            Limit to owned
          </label>
          {isManager && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.textMuted, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
              Show all tasks
            </label>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.textMuted, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
            Show archived
          </label>
          <input
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Search tasks…"
            style={{ ...iStyle, width: 160, padding: '5px 10px', fontSize: 12 }}
          />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ ...iStyle, width: 'auto', padding: '5px 8px', fontSize: 12, appearance: 'auto' }}>
            <option value="priority">Sort: Priority</option>
            <option value="due_date">Sort: Due date</option>
            <option value="created">Sort: Newest</option>
            <option value="effort">Sort: Effort</option>
            <option value="title">Sort: Title A–Z</option>
            <option value="assignee">Sort: Assignee</option>
          </select>
          <div style={{ display: 'flex', border: `1px solid ${theme.border}`, borderRadius: 6, overflow: 'hidden' }}>
            <button onClick={() => setViewMode('board')}
              style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                background: viewMode === 'board' ? theme.accent : 'transparent', color: viewMode === 'board' ? '#fff' : theme.textMuted }}>Board</button>
            <button onClick={() => setViewMode('list')}
              style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                background: viewMode === 'list' ? theme.accent : 'transparent', color: viewMode === 'list' ? '#fff' : theme.textMuted }}>List</button>
            <button onClick={() => setViewMode('myactions')}
              style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                background: viewMode === 'myactions' ? theme.accent : 'transparent', color: viewMode === 'myactions' ? '#fff' : theme.textMuted }}>My Actions</button>
          </div>
          <button onClick={() => setShowCreate(!showCreate)} style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: theme.accent, color: '#fff', border: 'none', cursor: 'pointer' }}>+ New Task</button>
        </div>
      </div>
      {showCreate && (
        <div style={{ background: theme.surface, border: `1px solid ${theme.accent}44`, borderRadius: 10, padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
            <div style={{ gridColumn: '1 / -1' }}><input value={form.title} onChange={e => setForm(p=>({...p, title: e.target.value}))} placeholder="Task title *" style={iStyle} /></div>
            <div style={{ gridColumn: '1 / -1' }}><textarea value={form.description} onChange={e => setForm(p=>({...p, description: e.target.value}))} placeholder="Description..." style={{...iStyle, height: 50, resize:'vertical'}} /></div>
            <select value={form.task_type} onChange={e => setForm(p=>({...p, task_type: e.target.value}))} style={{...iStyle, appearance:'auto'}}>
              {['development','testing','documentation','review','manufacturing','other'].map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
            </select>
            <UserSelect value={form.assigned_to || null} onChange={id => setForm(p=>({...p, assigned_to: id || ''}))} users={users} placeholder="Unassigned — type to search" />
            <select value={form.sprint} onChange={e => setForm(p=>({...p, sprint: e.target.value}))} style={{...iStyle, appearance:'auto'}}>
              <option value="">No sprint</option>
              {sprints.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input type="date" value={form.due_date} onChange={e => setForm(p=>({...p, due_date: e.target.value}))} style={iStyle} />
            {isManager && (
              <select value={form.priority} onChange={e => setForm(p=>({...p, priority: e.target.value}))} style={{...iStyle, appearance:'auto'}}>
                {['urgent','high','normal','low'].map(pr => <option key={pr} value={pr}>Priority: {PRIO_LABEL[pr]}</option>)}
              </select>
            )}
            <div style={{ gridColumn: '1 / -1' }}><FlaggedPicker value={form.flagged_users} onChange={v => setForm(p=>({...p, flagged_users: v}))} users={users} currentUserId={currentUser?.id} /></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ color: theme.textMuted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>Checklist (optional)</div>
              {(form.checklist || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input value={item} autoFocus
                    onChange={e => setForm(p => { const c = [...(p.checklist||[])]; c[i] = e.target.value; return {...p, checklist: c}; })}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setForm(p => ({...p, checklist: [...(p.checklist||[]), '']})); } }}
                    placeholder={`Item ${i+1}`} style={{ ...iStyle, flex: 1 }} />
                  <button onClick={() => setForm(p => ({...p, checklist: (p.checklist||[]).filter((_, j) => j !== i)}))}
                    style={{ padding: '0 10px', borderRadius: 6, background: 'transparent', color: theme.textDim, border: `1px solid ${theme.border}`, cursor: 'pointer', fontSize: 14 }}>×</button>
                </div>
              ))}
              <button onClick={() => setForm(p => ({...p, checklist: [...(p.checklist||[]), '']}))}
                style={{ padding: '5px 10px', borderRadius: 6, background: 'transparent', color: theme.accent, border: `1px dashed ${theme.accent}66`, cursor: 'pointer', fontSize: 12 }}>+ Add checklist item</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <button onClick={() => setShowCreate(false)} style={{ padding: '6px 12px', borderRadius: 6, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            <button onClick={createTask} disabled={saving} style={{ padding: '6px 12px', borderRadius: 6, background: theme.accent, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{saving ? 'Creating...' : 'Create'}</button>
          </div>
        </div>
      )}
      {viewMode === 'board' && (
      <div style={{ flex: 1, display: 'flex', gap: 10, overflow: 'auto' }}>
        {statusCols.map(col => {
          const colTasks = sortedTasks.filter(t => t.status === col.key);
          const isOver = dragOverCol === col.key && draggedTask?.status !== col.key;
          return (
            <div key={col.key} onDragOver={e => handleDragOver(e, col.key)} onDragLeave={handleDragLeave} onDrop={e => handleDrop(e, col.key)}
              style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 6, borderRadius: 10, padding: 6, transition: 'all 0.2s',
                background: isOver ? col.color + '12' : 'transparent', border: isOver ? `2px dashed ${col.color}66` : '2px dashed transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: theme.surface, borderRadius: 8, borderTop: `3px solid ${col.color}` }}>
                <span style={{ color: col.color, fontSize: 12, fontWeight: 700 }}>{col.label}</span>
                <span style={{ background: col.color+'22', color: col.color, fontSize: 10, padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>{colTasks.length}</span>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, overflow: 'auto', minHeight: 60 }}>
                {colTasks.map(t => (
                  <div key={t.id} draggable onDragStart={e => handleDragStart(e, t)} onDragEnd={handleDragEnd} onClick={() => setSelectedTask(t)}
                    style={{ background: theme.surface, border: `1px solid ${draggedTask?.id === t.id ? col.color : theme.border}`, borderRadius: 8, padding: 10,
                      cursor: 'grab', transition: 'all 0.15s', borderLeft: t.is_work_order ? `3px solid ${theme.purple}` : `1px solid ${theme.border}`, boxShadow: theme.cardShadow }}
                    onMouseEnter={e => { if (!draggedTask) e.currentTarget.style.borderColor = col.color; }}
                    onMouseLeave={e => { if (!draggedTask) e.currentTarget.style.borderColor = theme.border; }}>
                    <div style={{ color: theme.text, fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                      {t.is_work_order && <span style={{ background: theme.purple+'22', color: theme.purple, fontSize: 8, padding: '1px 4px', borderRadius: 3, fontWeight: 700, marginRight: 4, verticalAlign: 'middle' }}>WO</span>}
                      {t.title}
                    </div>
                    {t.description && <div style={{ color: theme.textDim, fontSize: 10, marginBottom: 6, maxHeight: 30, overflow: 'hidden' }}>{t.description.substring(0,80)}</div>}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                      {prioColor(t.priority) && (
                        <span style={{ background: prioColor(t.priority) + '22', border: `1px solid ${prioColor(t.priority)}66`, color: prioColor(t.priority), fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>{PRIO_LABEL[t.priority]}</span>
                      )}
                      {t.checklist_progress && t.checklist_progress.total > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 4, padding: '1px 6px', fontSize: 9, color: t.checklist_progress.done === t.checklist_progress.total ? theme.green : theme.textMuted }}>
                          ☑ {t.checklist_progress.done}/{t.checklist_progress.total}
                        </span>
                      )}
                      {t.my_open_action_count > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: theme.yellow + '22', border: `1px solid ${theme.yellow}55`, borderRadius: 4, padding: '1px 6px', fontSize: 9, fontWeight: 700, color: theme.yellow }}>
                          ⚡ {t.my_open_action_count} for you
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: theme.textDim, fontSize: 10 }}>{t.assigned_to_name || ''}</span>
                      {t.due_date && <span style={{ color: theme.textDim, fontSize: 10 }}>{t.due_date}</span>}
                    </div>
                  </div>
                ))}
                {colTasks.length === 0 && !isOver && <div style={{ color: theme.textDim, fontSize: 10, textAlign: 'center', padding: 12, opacity: 0.5 }}>Drop tasks here</div>}
                {isOver && <div style={{ border: `2px dashed ${col.color}44`, borderRadius: 8, padding: 14, textAlign: 'center', color: col.color, fontSize: 11, fontWeight: 600 }}>Drop to move to {col.label}</div>}
              </div>
            </div>
          );
        })}
      </div>
      )}
      {viewMode === 'list' && (
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 110px 100px 120px', gap: 0, alignItems: 'center',
          padding: '8px 12px', background: theme.surface, borderRadius: 8, marginBottom: 6,
          fontSize: 11, fontWeight: 700, color: theme.textMuted, position: 'sticky', top: 0 }}>
          <div>Task</div><div>Assignee</div><div>Status</div><div>Due</div><div>Sprint</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {sortedTasks.map(t => {
            const col = statusCols.find(c => c.key === t.status);
            return (
              <div key={t.id} onClick={() => setSelectedTask(t)}
                style={{ display: 'grid', gridTemplateColumns: '1fr 130px 110px 100px 120px', gap: 0, alignItems: 'center',
                  padding: '9px 12px', background: theme.surface, border: `1px solid ${theme.border}`,
                  borderLeft: t.is_work_order ? `3px solid ${theme.purple}` : `1px solid ${theme.border}`,
                  borderRadius: 8, cursor: 'pointer', transition: 'all 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = (col?.color || theme.accent); }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; }}>
                <div style={{ color: theme.text, fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>
                  {prioColor(t.priority) && <span style={{ background: prioColor(t.priority)+'22', color: prioColor(t.priority), fontSize: 8, padding: '1px 4px', borderRadius: 3, fontWeight: 700, marginRight: 4, verticalAlign: 'middle', textTransform: 'uppercase' }} title={'Priority: ' + PRIO_LABEL[t.priority]}>{PRIO_LABEL[t.priority]}</span>}
                  {t.is_work_order && <span style={{ background: theme.purple+'22', color: theme.purple, fontSize: 8, padding: '1px 4px', borderRadius: 3, fontWeight: 700, marginRight: 4, verticalAlign: 'middle' }}>WO</span>}
                  {t.my_open_action_count > 0 && <span style={{ background: theme.yellow+'22', color: theme.yellow, fontSize: 8, padding: '1px 4px', borderRadius: 3, fontWeight: 700, marginRight: 4, verticalAlign: 'middle' }} title={t.my_open_action_count + ' open action item(s) for you'}>⚡{t.my_open_action_count}</span>}
                  {t.title}
                </div>
                <div style={{ color: theme.textDim, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{t.assigned_to_name || '—'}</div>
                <div><span style={{ color: col?.color || theme.textDim, fontSize: 10, fontWeight: 600 }}>{col?.label || t.status}</span></div>
                <div style={{ color: t.due_date ? theme.textDim : theme.textDim, fontSize: 11, opacity: t.due_date ? 1 : 0.4 }}>{t.due_date || '—'}</div>
                <div style={{ color: theme.textDim, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sprintName(t.sprint) || '—'}</div>
              </div>
            );
          })}
          {sortedTasks.length === 0 && <div style={{ color: theme.textDim, fontSize: 12, textAlign: 'center', padding: 24, opacity: 0.6 }}>No tasks match.</div>}
        </div>
      </div>
      )}
      {viewMode === 'myactions' && (
      <div style={{ flex: 1, overflow: 'auto' }}>
        {isManager && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[['mine', 'Mine'], ['everyone', 'Everyone']].map(([key, label]) => (
              <button key={key} onClick={() => setActionScope(key)}
                style={{ padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${actionScope === key ? theme.accent : theme.border}`,
                  background: actionScope === key ? theme.accent + '18' : 'transparent',
                  color: actionScope === key ? theme.accent : theme.textMuted }}>{label}</button>
            ))}
            <span style={{ color: theme.textDim, fontSize: 11, alignSelf: 'center' }}>
              {actionScope === 'everyone' ? "Everyone's open action items, by person" : 'Your open action items, by task'}
            </span>
          </div>
        )}
        {actionsLoading ? (
          <div style={{ color: theme.textDim, fontSize: 12, textAlign: 'center', padding: 24 }}>Loading…</div>
        ) : actionGroups.length === 0 ? (
          <div style={{ color: theme.textDim, fontSize: 13, textAlign: 'center', padding: 40 }}>
            🎉 No open action items{actionScope === 'everyone' ? ' for anyone' : ' assigned to you'}.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {actionGroups.map(group => (
              <div key={group.key}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingBottom: 4, borderBottom: `1px solid ${theme.border}` }}>
                  <span style={{ color: theme.text, fontSize: 13, fontWeight: 700 }}>{group.label}</span>
                  <span style={{ background: theme.accent + '22', color: theme.accent, fontSize: 10, padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>{group.items.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {group.items.map(a => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: theme.surface, border: `1px solid ${theme.border}`, borderLeft: prioColor(a.task_priority) ? `4px solid ${prioColor(a.task_priority)}` : `1px solid ${theme.border}`, borderRadius: 8 }}>
                      <input type="checkbox" checked={false} onChange={() => tickAction(a)} title="Mark done" style={{ cursor: 'pointer', width: 16, height: 16, flex: 'none' }} />
                      <span onClick={() => openTaskById(a.task_id)} title="Open task"
                        style={{ flex: 1, fontSize: 13, color: theme.text, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.text}</span>
                      {prioColor(a.task_priority) && (
                        <span style={{ background: prioColor(a.task_priority) + '22', color: prioColor(a.task_priority), fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{PRIO_LABEL[a.task_priority]}</span>
                      )}
                      <span style={{ color: theme.textDim, fontSize: 11, whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {actionScope === 'everyone' ? a.task_title : (a.sprint_name || '')}
                      </span>
                      {a.due_date && <span style={{ color: theme.textDim, fontSize: 11, whiteSpace: 'nowrap' }}>{a.due_date}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
